import {
  AUDIT_CATEGORY_LABELS,
  type AuditCategory,
  AuditCategory as AuditCategorySchema,
  formatDateTime,
  isoDateInSaoPaulo,
} from '@deizi/shared';
import { useState } from 'react';
import { Drawer } from '../../../components/Overlays';
import { EmptyState, ErrorBox, Skeleton } from '../../../components/States';
import { type AuditFilters, auditParams, useAudit, useAuditEntry } from '../../../lib/data-api';
import { useIsAdmin } from '../../../lib/session';

const PERIODS: { days: number | null; label: string }[] = [
  { days: 30, label: 'Últimos 30 dias' },
  { days: 90, label: 'Últimos 90 dias' },
  { days: 365, label: 'Último ano' },
  { days: null, label: 'Tudo' },
];

const CATEGORIES: (AuditCategory | null)[] = [null, ...AuditCategorySchema.options];

/** "06/10 11h40" */
const when = (iso: string) => formatDateTime(iso).replace(/\/\d{4} /, ' ');

/** "Auditoria" (board Dados-Auditoria). */
export function AuditTab() {
  const isAdmin = useIsAdmin();
  const [f, setF] = useState<AuditFilters>({ category: null, actorUserId: '', days: 90 });
  const params = auditParams(f, isoDateInSaoPaulo());
  const q = useAudit(params);
  const [openId, setOpenId] = useState<string | null>(null);
  const first = q.data?.pages[0];
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  const counts = first?.counts ?? {};
  const csv = new URLSearchParams(params);
  return (
    <section className="card flat">
      <div className="row between wrap" style={{ padding: '14px 18px', gap: 10 }}>
        <div className="seg" role="radiogroup" aria-label="Tipo" style={{ flexWrap: 'wrap' }}>
          {CATEGORIES.map((c) => (
            <button
              key={c ?? 'all'}
              type="button"
              role="radio"
              aria-checked={f.category === c}
              className={f.category === c ? 'on' : ''}
              onClick={() => setF({ ...f, category: c })}
            >
              {c ? AUDIT_CATEGORY_LABELS[c] : 'Tudo'}{' '}
              <span className="xs" style={{ opacity: 0.7 }}>
                {counts[c ?? 'all'] ?? 0}
              </span>
            </button>
          ))}
        </div>
        <div className="row wrap" style={{ gap: 8 }}>
          <select
            className="input"
            aria-label="Quem"
            style={{ width: 180 }}
            value={f.actorUserId}
            onChange={(e) => setF({ ...f, actorUserId: e.target.value })}
          >
            <option value="">Todas as pessoas</option>
            {(first?.people ?? []).map((p) => (
              <option key={p.userId} value={p.userId}>
                {p.name}
              </option>
            ))}
          </select>
          <select
            className="input"
            aria-label="Período"
            style={{ width: 170 }}
            value={f.days ?? ''}
            onChange={(e) => setF({ ...f, days: e.target.value ? Number(e.target.value) : null })}
          >
            {PERIODS.map((p) => (
              <option key={p.label} value={p.days ?? ''}>
                {p.label}
              </option>
            ))}
          </select>
          {isAdmin ? (
            <a className="btn" href={`/api/v1/exports/auditoria.csv?${csv.toString()}`} download>
              Exportar CSV
            </a>
          ) : null}
        </div>
      </div>
      {q.isPending ? <Skeleton rows={4} height={44} /> : null}
      {q.isError ? (
        <ErrorBox title="Não foi possível carregar a auditoria" onRetry={() => void q.refetch()} />
      ) : null}
      {q.isSuccess && rows.length === 0 ? (
        counts.all ? (
          <div className="col" style={{ alignItems: 'center', gap: 8, padding: '28px 16px' }}>
            <p className="muted sm">Nada com esses filtros.</p>
            <button
              type="button"
              className="btn sm"
              onClick={() => setF({ category: null, actorUserId: '', days: null })}
            >
              Limpar filtros
            </button>
          </div>
        ) : (
          <EmptyState title="Nenhum registro ainda">
            Mudanças de preço, pedidos e exclusões aparecem aqui com quem fez e quando.
          </EmptyState>
        )
      ) : null}
      {rows.length ? (
        <div className="gt" style={{ overflowX: 'auto' }}>
          <div
            className="gtr head"
            style={{ gridTemplateColumns: '100px 140px 1.2fr 2fr', minWidth: 640 }}
          >
            <span>Quando</span>
            <span>Quem</span>
            <span>O quê</span>
            <span>Detalhe</span>
          </div>
          {rows.map((r) => (
            <button
              key={r.id}
              type="button"
              className="gtr"
              style={{
                gridTemplateColumns: '100px 140px 1.2fr 2fr',
                minWidth: 640,
                textAlign: 'left',
                background: r.id === openId ? 'var(--pe-soft)' : undefined,
              }}
              onClick={() => setOpenId(r.id)}
            >
              <span className="tab sm">{when(r.at)}</span>
              <span className="sm">{r.actor}</span>
              <span style={{ fontWeight: 500 }}>{r.title}</span>
              <span className="sm muted">{r.summary}</span>
            </button>
          ))}
        </div>
      ) : null}
      <div className="row between" style={{ padding: '10px 18px 16px' }}>
        <span className="xs muted">
          {rows.length} {rows.length === 1 ? 'registro' : 'registros'}
          {q.hasNextPage ? ' (há mais)' : ''}
        </span>
        {q.hasNextPage ? (
          <button
            type="button"
            className="btn sm"
            disabled={q.isFetchingNextPage}
            onClick={() => void q.fetchNextPage()}
          >
            Carregar mais
          </button>
        ) : null}
      </div>
      {openId ? <AuditDrawer id={openId} onClose={() => setOpenId(null)} /> : null}
    </section>
  );
}

function AuditDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useAuditEntry(id);
  const e = q.data;
  return (
    <Drawer
      kicker={e ? `${formatDateTime(e.at)} · ${e.actor}` : ''}
      title={e?.title ?? 'Registro'}
      onClose={onClose}
    >
      {q.isPending ? <Skeleton rows={3} height={36} /> : null}
      {e ? (
        <div className="col" style={{ gap: 12 }}>
          <span className="badge" style={{ alignSelf: 'flex-start' }}>
            {AUDIT_CATEGORY_LABELS[e.category]}
          </span>
          <p>{e.summary}</p>
          {e.changes.length ? (
            <div className="gt">
              <div className="gtr head" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                <span>Campo</span>
                <span>Antes</span>
                <span>Depois</span>
              </div>
              {e.changes.map((c) => (
                <div key={c.field} className="gtr" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                  <span className="sm">{c.label}</span>
                  <span className="sm muted">{c.before ?? '—'}</span>
                  <span className="sm">{c.after ?? '—'}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="sm muted">Sem campos para comparar.</p>
          )}
          {e.viaToken ? <p className="xs muted">Feito por um token de API.</p> : null}
        </div>
      ) : null}
    </Drawer>
  );
}
